/** @format */

import { SHIPPING_SPEED_DETAILS } from "@outstanding/shared";
import { formatCentsCompact } from "@/utils/currency";
import { getFreeShippingMinSubtotal } from "@/utils/shipping";

/**
 * The ONE SPA source for shipping facts stated in customer-facing copy
 * (bead outstanding-qbo.7): transit windows, shipping prices, and the
 * free-ground-shipping threshold. Faq.vue and Terms.vue consume these instead
 * of each hand-writing "$10", "5-7 business days", etc. (The collection FAQ
 * copy is built in src/lib/collectionFaq.ts from the same @outstanding/shared
 * constants — that module is also bundled by the Pages Functions prerender, so
 * it can't import this Vue-reactive one.)
 *
 * The values come straight from @outstanding/shared — the SAME
 * SHIPPING_SPEED_DETAILS + FREE_SHIPPING_MIN_SUBTOTAL the API prices orders
 * from and serves via GET /api/shipping-options (bead 49n.3a). A server-side
 * change to a duration or price now flows here directly, so static copy can't
 * strand stale numbers — the old shipping-facts.json fixture + its twin tests
 * retire.
 *
 * NOT covered here: the checkout flow itself, which renders the speeds SERVED
 * by GET /api/shipping-options (useCheckout) — this module is for static copy
 * surfaces that render without that fetch.
 */

/** Ground transit window, e.g. "5-7 business days" (FREE rides the same service). */
export const GROUND_TRANSIT_DAYS: string = SHIPPING_SPEED_DETAILS.GROUND.days;

/** Priority transit window, e.g. "2-3 business days". */
export const PRIORITY_TRANSIT_DAYS: string = SHIPPING_SPEED_DETAILS.PRIORITY.days;

/** Ground shipping price in integer cents. */
const GROUND_COST_CENTS: number = SHIPPING_SPEED_DETAILS.GROUND.cost;

/** Priority shipping price in integer cents. */
const PRIORITY_COST_CENTS: number = SHIPPING_SPEED_DETAILS.PRIORITY.cost;

/** Ground price as compact copy, e.g. "$10". */
export const GROUND_COST_TEXT: string = formatCentsCompact(GROUND_COST_CENTS);

/** Priority price as compact copy, e.g. "$25". */
export const PRIORITY_COST_TEXT: string = formatCentsCompact(PRIORITY_COST_CENTS);

/**
 * The free-shipping threshold as compact copy, e.g. "$50" — REACTIVE: derives
 * from getFreeShippingMinSubtotal(), so copy rendered inside a computed picks
 * up the value served by GET /api/shipping-options when it lands (the fixture
 * value is the compiled-in fallback behind that getter).
 */
export function freeShippingThresholdText(): string {
  return formatCentsCompact(getFreeShippingMinSubtotal());
}

/**
 * Production window before an order ships ("completed and dispatched within
 * 7 business days"). Owner-stated lab SLA — not derived from server constants
 * (nothing server-side knows it), just centralized so Faq/Terms state the same
 * number.
 */
export const PRODUCTION_WINDOW_DAYS = "7 business days";
